import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { browser } from '@atlaskit/editor-common/browser';
import type {
	Command,
	CommandDispatch,
	FloatingToolbarButton,
	FloatingToolbarCustom,
	Icon,
} from '@atlaskit/editor-common/types';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Pressable } from '@atlaskit/primitives/compiled';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../../types/types';
import type { EditorPluginAIConfigItem } from '../../prebuilt/config-items/config-items';
import { improveWriting } from '../../prebuilt/config-items/prompts/enhance/enhance';
import { improveWritingADF } from '../../prebuilt/config-items/prompts/improve-writing-adf/improve-writing-adf';
import { SELECTION_CATEGORY } from '../../utils/analytics/enums';
import { getSelectionCategoryUsingState } from '../../utils/selection';
import type { Positions } from '../../utils/xstate/get-ai-experience-service';

export const floatingToolbarAIButtonTestID = `ak-editor-ai-selection-toolbar-ai-button`;

const styles = cssMap({
	askAIInputButton: {
		cursor: 'text',
		display: 'flex',
		alignItems: 'center',
		minHeight: '32px',
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('border.radius'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		backgroundColor: token('color.background.input'),
		transition: 'background-color 0.2s ease-in-out',
	},
	askAIInputButtonEnabled: {
		color: token('color.text.subtlest'),
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
		},
	},
	askAIInputButtonDisabled: {
		color: token('color.text.disabled'),
		cursor: 'not-allowed',
		border: `${token('border.width')} solid ${token('color.border.disabled')}`,
	},
});
export const getAIExperienceButton = ({
	intl,
	rangeBaseGenerate,
	hideTitle,
	aiGlobalOptIn,
	icon,
	title,
	tooltip,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
}: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	hideTitle?: boolean;
	aiGlobalOptIn: AIGlobalOptIn;
	icon?: Icon;
	title?: MessageDescriptor;
	tooltip?: MessageDescriptor;
	triggeredFor?: NodeType;
	pulse?: boolean;
	onMount?: () => void;
	onClick?: () => void;
	disabled: boolean;
}) => {
	let didMouseEnter = false;

	const metaKey = browser.mac ? '⌘' : 'Ctrl';

	return {
		type: 'button',
		pulse,
		showTitle: true,
		testId: floatingToolbarAIButtonTestID,
		icon: icon ? icon : AtlassianIntelligenceIcon,
		...(!hideTitle && {
			title: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		}),
		tooltipContent: intl.formatMessage(tooltip ?? sharedMessages.aiUniversalShortCallToAction, {
			key: metaKey,
		}),
		onMount,
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: rangeBaseGenerate,
				lastTriggeredFrom: 'floatingToolbar',
				// ED-18194 Added analytics for floating toolbar to determine
				// the % of users who click the button vs shift tab to it
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				triggeredFor,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			if (onClick) {
				onClick();
			}

			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

const getImproveWritingPrompt = (state: EditorState) => {
	const positions: Positions = [state.selection.from, state.selection.to];
	const selectionStrategy = getSelectionCategoryUsingState(state, positions);

	const fullPageSelectionCategories = [SELECTION_CATEGORY.FULL_DOCUMENT, SELECTION_CATEGORY.EMPTY];
	const improveWritingPrompt =
		fullPageSelectionCategories.includes(selectionStrategy) &&
		expValEqualsNoExposure('platform_editor_ai_iw_adf_streaming', 'isEnabled', true)
			? improveWritingADF
			: improveWriting;
	return improveWritingPrompt;
};

export const getImproveWritingAIButton = ({
	intl,
	hideTitle,
	aiGlobalOptIn,
	disabled,
}: {
	intl: IntlShape;
	hideTitle?: boolean;
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
}) => {
	let didMouseEnter = false;

	return {
		type: 'button',
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-improve-writing`,
		icon:
			// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
			fg('ai-smart-button-team-25') || editorExperiment('platform_editor_controls', 'variant1')
				? MagicWandIcon
				: undefined,
		...(!hideTitle && {
			title: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		}),
		tooltipContent: intl.formatMessage(aiMessages.improveWritingToolbarIconTooltip),
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: getImproveWritingPrompt(state),
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

export const getAskAIButton = ({
	intl,
	rangeBaseGenerate,
	aiGlobalOptIn,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
}: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	aiGlobalOptIn: AIGlobalOptIn;
	triggeredFor?: NodeType;
	pulse?: boolean;
	onMount?: () => void;
	onClick?: () => void;
	disabled: boolean;
}) => {
	const Icon = () => <RovoIcon size="small"></RovoIcon>;
	return getAIExperienceButton({
		intl,
		rangeBaseGenerate,
		hideTitle: false,
		aiGlobalOptIn,
		icon: Icon,
		title: aiMessages.askAIToolbarIconTitle,
		tooltip: editorExperiment('platform_editor_ai_quickstart_command', true)
			? aiMessages.askAIToolbarIconTooltipQuickCommand
			: aiMessages.askAIToolbarIconTooltip,
		triggeredFor,
		pulse,
		onMount,
		onClick,
		disabled,
	});
};

export const getAskAIInputButton = (props: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	aiGlobalOptIn: AIGlobalOptIn;
	triggeredFor?: NodeType;
	disabled: boolean;
}): FloatingToolbarCustom<Command> => {
	const { intl, rangeBaseGenerate, disabled, aiGlobalOptIn } = props;

	return {
		type: 'custom',
		fallback: [getAskAIButton(props)],
		render: (view) =>
			view && (
				<Pressable
					testId={floatingToolbarAIButtonTestID}
					isDisabled={disabled}
					xcss={cx(
						styles.askAIInputButton,
						disabled && styles.askAIInputButtonDisabled,
						!disabled && styles.askAIInputButtonEnabled,
					)}
					onClick={() => {
						const openAIModalCommand = createOpenAIModalCommand({
							state: view.state,
							configItem: rangeBaseGenerate,
							lastTriggeredFrom: 'floatingToolbarDropdown',
							// ED-18194 Added analytics for floating toolbar to determine
							// the % of users who click the button vs shift tab to it
							// triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
							aiGlobalOptIn,
							locale: intl.locale,
						});

						openAIModalCommand(view.state, view.dispatch);
					}}
				>
					{intl.formatMessage(aiMessages.askRovoInputButtonLabel)}
				</Pressable>
			),
	};
};
